import type {
  CalendarioDeTienda,
  EstadoDeLectura,
  EstadoDeSalud,
  Lector,
  TipoDeLectura,
  UnidadDeFrecuencia,
} from '../types/prixia.types';

/** Los textos del panel del catálogo de Prixia (plan 297), en castellano. */
type Variante = 'success' | 'warning' | 'destructive' | 'secondary' | 'outline' | 'info';

export const TIPO_DE_LECTURA: Record<TipoDeLectura, string> = {
  PRICE_CHECK: 'Chequeo de precios',
  FULL_RESCRAPE: 'Barrido del catálogo',
};

export const ESTADO_DE_LECTURA: Record<EstadoDeLectura, string> = {
  QUEUED: 'En cola',
  RUNNING: 'Leyendo',
  COMPLETED: 'Terminada',
  FAILED: 'Falló',
  CANCELLED: 'Cancelada',
};

export const VARIANTE_DE_LECTURA: Record<EstadoDeLectura, Variante> = {
  QUEUED: 'secondary',
  RUNNING: 'info',
  COMPLETED: 'success',
  FAILED: 'destructive',
  CANCELLED: 'outline',
};

export const SALUD: Record<EstadoDeSalud, { texto: string; variante: Variante }> = {
  AL_DIA: { texto: 'Al día', variante: 'success' },
  CON_AVISO: { texto: 'Con aviso', variante: 'warning' },
  FALLANDO: { texto: 'Fallando', variante: 'destructive' },
  SIN_LECTURAS: { texto: 'Sin lecturas', variante: 'secondary' },
  APAGADA: { texto: 'Apagada', variante: 'outline' },
};

const UNIDADES: Record<UnidadDeFrecuencia, [string, string]> = {
  HOURS: ['hora', 'horas'],
  DAYS: ['día', 'días'],
  WEEKS: ['semana', 'semanas'],
};

export function frecuenciaEnTexto(
  c:
    | Partial<
        Pick<
          CalendarioDeTienda,
          'intervalValue' | 'intervalUnit' | 'preferredHour' | 'preferredMin' | 'isActive'
        >
      >
    | undefined
): string {
  if (!c || c.intervalValue === undefined || c.intervalUnit === undefined) return 'Sin programar';
  if (c.isActive === false) return 'Apagado';
  const [uno, varios] = UNIDADES[c.intervalUnit];
  const cada = c.intervalValue === 1 ? `Cada ${uno}` : `Cada ${c.intervalValue} ${varios}`;
  const hora =
    c.preferredHour != null
      ? ` · ${String(c.preferredHour).padStart(2, '0')}:${String(c.preferredMin ?? 0).padStart(2, '0')}`
      : '';
  return cada + hora;
}

export function fechaYHora(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-BO', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function duracion(desde: string | null, hasta: string | null): string {
  if (!desde || !hasta) return '—';
  const ms = new Date(hasta).getTime() - new Date(desde).getTime();
  if (ms < 60_000) return `${Math.max(1, Math.round(ms / 1000))} s`;
  const minutos = Math.round(ms / 60_000);
  if (minutos < 60) return `${minutos} min`;
  return `${Math.floor(minutos / 60)} h ${minutos % 60} min`;
}

/**
 * «Cómo se lee», en castellano llano (pulido de la S2). El servidor manda el nombre
 * técnico de cada lector («Navegador (Puppeteer)», «Dismac Bolivia (Magento GraphQL)»):
 * acá se muestra lo que hace, sin jerga ni el nombre de la tienda para la que se hizo
 * (no es un lector «del cliente»). Los que no están en la lista son «Lector especial».
 */
const ETIQUETA_DE_LECTOR: Record<string, string> = {
  auto: 'Detectar sola',
  shopify: 'Lector para tiendas Shopify',
  vtex: 'Lector para tiendas VTEX',
  magento: 'Lector para tiendas Magento',
  woocommerce: 'Lector para tiendas WooCommerce',
  puppeteer: 'Navegador (más lento)',
};

export const LECTOR_ESPECIAL = 'Lector especial';

const tieneEtiqueta = (tipo: string) =>
  Object.prototype.hasOwnProperty.call(ETIQUETA_DE_LECTOR, tipo);

export function nombreDeLector(tipo: string): string {
  return tieneEtiqueta(tipo) ? ETIQUETA_DE_LECTOR[tipo] : LECTOR_ESPECIAL;
}

/**
 * La lista para elegir: cada lector con su etiqueta llana. Si hay más de un «Lector
 * especial», se numeran (en el orden en que vienen) para que no se vean iguales.
 */
export function etiquetasDeLectores(lectores: Lector[]): Lector[] {
  const especiales = lectores.filter((l) => !tieneEtiqueta(l.type)).length;
  let n = 0;
  return lectores.map((l) => {
    if (tieneEtiqueta(l.type)) return { type: l.type, name: ETIQUETA_DE_LECTOR[l.type] };
    n++;
    return { type: l.type, name: especiales > 1 ? `${LECTOR_ESPECIAL} ${n}` : LECTOR_ESPECIAL };
  });
}

/**
 * L7 ronda B (punto 19): la moneda de una tienda se escribe con las tres letras de su
 * código (ISO 4217). Vacía = no se sabe. Devuelve el código en mayúsculas, '' si está
 * vacía, o null si no es un código. Ronda C (punto 44): tiene que ser una moneda de
 * verdad, no cualquier trío de letras (XYZ no vale).
 */
// `Intl.supportedValuesOf` es ES2022 y este paquete compila con lib ES2020: la firma
// va aparte, la llamada es la misma que en el backend.
const MONEDAS_ISO: string[] = (
  Intl as unknown as { supportedValuesOf(clave: 'currency'): string[] }
).supportedValuesOf('currency');

export function codigoDeMoneda(texto: string): string | null {
  const c = texto.trim().toUpperCase();
  if (c === '') return '';
  return /^[A-Z]{3}$/.test(c) && MONEDAS_ISO.includes(c) ? c : null;
}
